/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/require-default-props */
import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useImperativeHandle
} from 'react';
import { useIntl } from 'react-intl';
import {
  AppContext,
  isAuthorised,
  Loader,
  getDefaultValue,
  updateConditions,
  EventEmitter,
  useProduct,
  SuperTable,
  getTranslatedText
} from '@nile/components';
import {
  AccountClient,
  GET_SERVICE_CATALOG_ITEMS,
  GET_SERVICE_CATALOG_ITEMS_FOR_TABLE,
  GET_SERVICE_CATEGORIES,
  GET_SERVICE_ITEMS_FOR_IT
} from '@nile/graphql';
import { TextCell } from '@nile/mangifera/src/Components/Ticket';
import styled from 'styled-components';
import { ServiceCatalogItem } from '@nile/types';
import {
  PriceCell,
  LinkCell,
  ServiceItemTypeCell,
  ActionsCell
} from '@nile/banyan/src/Pages/ServiceCatalogNew/ProductsAndServicesTable/Cells';

const ROW_COUNT = 20;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
`;

const generateOptions = (col, data) => {
  switch (col?.name) {
    case 'category.name':
      return data.getServiceCategories.map((el) => {
        return {
          ...el,
          name: el.categoryId,
          value: el.name,
          label: el.name
        };
      });
    default:
      return null;
  }
};

const getDynamicQuery = (col) => {
  switch (col?.name) {
    case 'category.name':
      return AccountClient.query({
        query: GET_SERVICE_CATEGORIES
      });
    default:
      return null;
  }
};

const checkDynamicQuery = (col) => {
  switch (col?.name) {
    case 'category.name':
      return true;
    default:
      return false;
  }
};

const getFilterType = (name) => {
  switch (name) {
    case 'category.name':
      return 'select';
    default:
      return 'text';
  }
};

const getAccessor = (col) => {
  switch (col.name) {
    case 'afterHoursUnitPrice':
      return 'sellingPrice';
    default:
      return col.name;
  }
};

const getColumnSize = (col) => {
  switch (col.name) {
    case 'delete':
      return 50;
    case 'name':
    case 'invoiceDescription':
      return 200;
    default:
      return 150;
  }
};

const getType = (col) => {
  switch (col.name) {
    case 'name':
      return 'Link';
    case 'costPrice':
    case 'sellingPrice':
    case 'afterHoursUnitPrice':
      return 'Price';
    case 'itemType':
      return 'ServiceItemType';
    case 'delete':
      return 'Action';
    default:
      return 'Text';
  }
};

const isEditable = (col) => {
  switch (col.name) {
    case 'costPrice':
    case 'sellingPrice':
      return true;
    default:
      return false;
  }
};

const isSortable = (col) => {
  switch (col.name) {
    case 'delete':
    case 'costPrice':
    case 'sellingPrice':
    case 'afterHoursUnitPrice':
      return false;
    default:
      return true;
  }
};

const isFilterable = (col) => {
  switch (col?.name) {
    case 'delete':
    case 'costPrice':
    case 'sellingPrice':
    case 'afterHoursUnitPrice':
      return false;
    default:
      return true;
  }
};

const getDataType = (name) => {
  switch (name) {
    case 'costPrice':
    case 'sellingPrice':
    case 'afterHoursUnitPrice':
      return 'number';
    default:
      return 'string';
  }
};

const getSortAttributeName = (column) => {
  switch (column?.name) {
    case 'costPrice':
      return 'costPrice.details[0].value';
    case 'sellingPrice':
      return 'sellingPrice.details[0].value';
    case 'afterHoursUnitPrice':
      return 'sellingPrice.details[0].afterHoursValue';
    default:
      return column?.name;
  }
};

const cols = [
  {
    name: 'name',
    label: 'admin.serviceCatalog.list.serviceItemName',
    dataType: getDataType('name')
  },
  {
    name: 'invoiceDescription',
    label: 'admin.serviceCatalog.list.itemDescription',
    dataType: getDataType('invoiceDescription')
  },
  {
    name: 'itemType',
    label: 'common.type',
    // label: 'admin.serviceCatalog.list.itemType',
    dataType: getDataType('itemType')
  },
  // {
  //   name: 'category.name',
  //   label: 'admin.serviceCatalog.list.category',
  //   dataType: getDataType('category.name')
  // },
  {
    name: 'costPrice',
    label: 'admin.serviceCatalog.offeringType.quantity.cost',
    // label: 'admin.serviceCatalog.list.costPrice',
    dataType: getDataType('costPrice'),
    mspOnly: true
  },
  {
    name: 'sellingPrice',
    label: 'admin.serviceCatalog.offeringType.quantity.price',
    // label: 'admin.serviceCatalog.list.sellingPrice',
    dataType: getDataType('sellingPrice'),
    mspOnly: true
  }
  // {
  //   name: 'afterHoursUnitPrice',
  //   label: 'admin.serviceCatalog.list.afterHourPrice',
  //   dataType: getDataType('afterHoursUnitPrice'),
  //   mspOnly: true
  // },
  // {
  //   name: 'delete',
  //   label: '',
  //   dataType: getDataType('delete'),
  //   mspOnly: true
  // }
];

interface ServiceItemListTableProps {
  mode?: 'VIEW' | 'EDIT';
  records?: ServiceCatalogItem[];
  filterItems?: string[];
  setRecords?: (value: ServiceCatalogItem[]) => void;
  setShowReset?: (val) => void;
  actions?: {
    editServiceItem?: (item) => void;
    cloneServiceItem?: (item) => void;
    toggleServiceItemStatus?: (item) => void;
    updateServiceItem?: (item) => Promise<unknown>;
    deleteServiceItem?: (
      arg
    ) => Promise<{
      data: {
        deleteServiceItem: boolean;
      };
    }>;
    deleteServiceItems?: (
      arg
    ) => Promise<{
      data: {
        deleteServiceItems: boolean;
      };
    }>;
  };
  actionsRef?: React.Ref<any>;
  defaultConditions?: Object;
}

interface Listinfo {
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
  totalCount?: number;
  condition?: JSON;
  sort?: [
    {
      order: 'ASC' | 'DESC';
      attribute: string;
    }
  ];
}

function ServiceItemListTable({
  mode,
  actions,
  actionsRef,
  filterItems,
  setShowReset,
  records,
  setRecords,
  defaultConditions
}: ServiceItemListTableProps) {
  const loading = false;
  const intl = useIntl();

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'Name', columns: [] }
  ]);
  const [data, setData] = useState([]);
  const isMSPProduct = useProduct('MSP');
  /**
   * Default options for the list info to use in table
   */
  const [options, setOptions] = useState({
    pageSize: ROW_COUNT,
    page: 1,
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ],
    condition: {
      joinOperator: 'AND',
      operands: [{ ...defaultConditions }]
    }
  });

  const [listLoading, setListLoading] = useState<boolean>(false);
  const [listInfo, setListInfo] = useState<Listinfo>({
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ]
  });
  const listInfoByRef = useRef(null);
  listInfoByRef.current = listInfo;
  const appContext = useContext<any>(AppContext);
  const { user } = appContext;
  const roleFeatures = user?.role?.roleFeatureList || [];
  const isEditAuthorised =
    mode === 'EDIT' && isAuthorised(roleFeatures, 'serviceCatalog.edit');
  const isDeleteAuthorised = isAuthorised(
    roleFeatures,
    'serviceCatalog.delete'
  );

  const getActions = (col) => {
    switch (col.name) {
      case 'name':
        return {
          onClick: actions?.editServiceItem
        };
      case 'costPrice':
      case 'sellingPrice':
        return {
          save: actions?.updateServiceItem
        };
      // case 'delete':
      //   return {
      //     onClick: deleteItem,
      //     checkDeleteItemAllowPermission: checkDeleteItemAllowPermission
      //   };
      case 'delete':
        return {
          delete: actions?.deleteServiceItem,
          clone: actions?.cloneServiceItem,
          toggleStatus: actions?.toggleServiceItemStatus
        };
      default:
        return {};
    }
  };

  /**
   * Method to get Service items.
   * Call this method to fetch/refetch the data on mounting/sorting/filtering
   * @param {object} personalization
   * @param {object} pageOptions
   */
  const fetchViewData = async (
    personalization?: JSON | null,
    pageOptions?: {
      pageSize: number;
      page: number;
      sort:
        | [
            {
              attribute: string;
              order: 'ASC' | 'DESC';
            }
          ]
        | null;
      condition: JSON | null;
    }
  ): Promise<void> => {
    setListLoading(true);
    const response = await AccountClient.query({
      query: GET_SERVICE_CATALOG_ITEMS_FOR_TABLE,
      variables: {
        isMSPProduct,
        listInfo: pageOptions
          ? {
              pageSize:
                pageOptions.pageSize || listInfo?.pageSize || options.pageSize,
              page: pageOptions.page || listInfo?.page || options.page,
              sort: pageOptions.sort || null,
              condition: pageOptions.condition || listInfo?.condition || null
            }
          : {
              ...options,
              sort: listInfo.sort ||
                options.sort || [
                  {
                    attribute: 'createdTime',
                    order: 'DESC'
                  }
                ]
            }
      },
      fetchPolicy: 'network-only'
    });
    setListLoading(false);
    const columns = cols
      .filter((col) => {
        if (col?.mspOnly) {
          return !!isMSPProduct;
        }
        return true;
      })
      .map((col, idx) => {
        return {
          field: col,
          id: col.name,
          type: getType(col),
          className: 'sticky',
          disableResizing: false,
          size: getColumnSize(col),
          actions: mode === 'EDIT' && getActions(col),
          filterType: mode === 'EDIT' && getFilterType(col),
          editable: mode === 'EDIT' && isEditable(col),
          accessorKey: getAccessor(col),
          disableSortBy: mode === 'VIEW' || !isSortable(col),
          disableFilters: mode === 'VIEW' || !isFilterable(col),
          minWidth: col.name === 'name' ? 200 : 50,
          pinned: col.name === 'delete' ? 'right' : false,
          header: col?.label ? getTranslatedText(intl, col?.label) : ' ',
          helpers: {
            getDefaultValue,
            updateConditions: updateAllConditions,
            checkDynamicQuery,
            getDynamicQuery,
            generateOptions,
            filterMeta: cols.map((col) => {
              return { ...col, filterType: getFilterType(col.name) };
            }) // to override datatype based filter block addition
          }
        };
      });

    setColumnGroup([{ Header: 'Name', header: 'Name', columns }]);
    if (response?.data?.getServiceCatalogItems) {
      const filteredItems = response.data.getServiceCatalogItems.items?.filter(
        (item) => {
          return !filterItems?.includes(item?.itemId);
        }
      );
      setData(filteredItems);
      setListInfo(response.data.getServiceCatalogItems.listInfo);
    }
  };

  const setConditions = (condition) => {
    fetchViewData(undefined, { ...listInfoByRef.current, page: 1, condition });
  };

  const updateAllConditions = (condition, selectedField) => {
    updateConditions({
      condition,
      selectedField,
      viewConditions: listInfoByRef?.current?.condition || null, // any variable in which conditions are stored for this component
      setConditions: setConditions
    });
  };

  const fetchFilteredData = (text) => {
    const condition = {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'name',
          operator: 'contains',
          value: text
        },
        { ...defaultConditions }
      ]
    };
    fetchViewData(null, { condition: condition });
  };

  useEffect(() => {
    EventEmitter.subscribe('filterData', fetchFilteredData);
    EventEmitter.subscribe('resetServiceCatalogFilters', resetFilter);
    return () => {
      EventEmitter.unsubscribe('filterData');
      EventEmitter.unsubscribe('resetServiceCatalogFilters');
    };
  }, []);

  useEffect(() => {
    if (listInfo?.condition && setShowReset) setShowReset(true);
    if (!listInfo?.condition && setShowReset) setShowReset(false);
  }, [listInfo.condition]);

  useEffect(() => {
    if (defaultConditions) {
      setConditions({
        joinOperator: 'AND',
        operands: [{ ...defaultConditions }]
      });
    } else {
      fetchViewData();
    }
  }, [defaultConditions]);

  useEffect(() => {
    EventEmitter.subscribe('filterData', fetchFilteredData);
    return () => {
      EventEmitter.unsubscribe('filterData');
    };
  }, []);

  useImperativeHandle(actionsRef, () => {
    return {
      refetchTable: fetchViewData
    };
  });

  if (loading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const defaultColumnCells = {
    Price: PriceCell,
    Link: isEditAuthorised ? LinkCell : TextCell,
    ServiceItemType: ServiceItemTypeCell,
    Action: ActionsCell
  };

  const resetFilter = () => {
    setListInfo({});
    fetchViewData(undefined, { ...listInfoByRef.current, condition: null });
  };

  const context = {
    setRecords: setRecords,
    viewConditions: listInfoByRef?.current?.condition || null,
    records
  };

  return (
    <SuperTable
      padded="sm"
      idKey="itemId"
      setData={setData}
      context={context}
      data={data || []}
      listInfo={listInfo}
      maintainSelection
      rowCount={ROW_COUNT}
      columns={columnGroup}
      isLoading={listLoading}
      refreshTable={fetchViewData}
      defaultColumnCells={defaultColumnCells}
      getAttributeName={getSortAttributeName}
    />
  );
}

export { ServiceItemListTable };
